import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { directionFor, type Locale, type SessionUser } from '@qms360/shared';
import { ApiError, fetchMe, login as apiLogin, logout as apiLogout, sessionCache, tokenStore } from './api.js';
import { setUiLocale } from './i18n.js';
import { t } from './i18n.js';

/**
 * Session context.
 *
 * ADR-0015: this is a CACHE OF A SERVER DECISION, not the decision itself. Every
 * API call is re-authorized server-side regardless of what this holds. If the two
 * ever disagree, the server is right — which is why `refresh()` re-reads rather
 * than patching local state.
 *
 * ADR-0014: locale drives the document direction here, at the root, so no component
 * needs to know or assume which way the page runs.
 */

interface SessionState {
  user: SessionUser | null;
  /**
   * What the server says this person may do, per app, anywhere in their scope.
   * Null when unknown (an offline start): screens then show their actions, and
   * the server decides as always.
   */
  capabilities: Record<string, string[]> | null;
  /**
   * True when the server could not be reached and the user shown is the one
   * this device last signed in as. Everything still re-checks with the server
   * once a signal returns.
   */
  offline: boolean;
  loading: boolean;
  error: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

const SessionContext = createContext<SessionState | null>(null);

function applyLocale(locale: Locale): void {
  const root = document.documentElement;
  root.lang = locale;
  root.dir = directionFor(locale);
  // The words follow the same setting as the direction (pass 12).
  setUiLocale(locale);
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [capabilities, setCapabilities] = useState<Record<string, string[]> | null>(null);
  const [offline, setOffline] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = async (): Promise<void> => {
    if (!tokenStore.access) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const me = await fetchMe();
      setUser(me.user);
      setCapabilities(me.capabilities ?? null);
      setOffline(false);
      sessionCache.write(me.user);
      applyLocale(me.user.locale);
    } catch (caught) {
      if (caught instanceof ApiError && (caught.status === 401 || caught.status === 403)) {
        // The server says the session is gone — revoked, expired, or the account
        // archived. Signed out.
        tokenStore.clear();
        sessionCache.clear();
        setUser(null);
        setOffline(false);
      } else {
        // No answer from the server is not a verdict (DATA-1). Start as the
        // person this device last signed in as, and check again with a signal.
        const cached = sessionCache.read<SessionUser>();
        if (cached) {
          setUser(cached);
          setOffline(true);
          applyLocale(cached.locale);
        } else {
          setUser(null);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
    // Re-check the moment a signal returns: an offline start is provisional.
    const recheck = () => void refresh();
    window.addEventListener('online', recheck);
    return () => window.removeEventListener('online', recheck);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo<SessionState>(
    () => ({
      user,
      capabilities,
      offline,
      loading,
      error,
      async signIn(email, password) {
        setError(null);
        try {
          const result = await apiLogin(email, password);
          setUser(result.user);
          setOffline(false);
          sessionCache.write(result.user);
          applyLocale(result.user.locale);
          // Capabilities come with the session check, not the sign-in answer.
          void fetchMe()
            .then((me) => setCapabilities(me.capabilities ?? null))
            .catch(() => setCapabilities(null));
        } catch (caught) {
          setError(caught instanceof Error ? caught.message : t("Sign in failed."));
          throw caught;
        }
      },
      async signOut() {
        await apiLogout();
        sessionCache.clear();
        setUser(null);
        setCapabilities(null);
        setOffline(false);
        // The sign-in screen is for whoever comes next: back to the default (pass 12).
        applyLocale('en');
      },
      refresh,
    }),
    [user, capabilities, offline, loading, error],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

/**
 * Whether to offer an action: true if the person may do it somewhere in this
 * app, or if that is not known. Hiding a button is a courtesy; the server
 * still decides every request, record by record.
 */
export function useCan(app: string, permission: string): boolean {
  const { capabilities } = useSession();
  return capabilities ? (capabilities[app] ?? []).includes(permission) : true;
}

export function useSession(): SessionState {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error(t("useSession must be used inside a SessionProvider."));
  }
  return context;
}
